import React, { useRef, useCallback } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill-emoji/dist/quill-emoji.css';
import Emoji from 'react-quill-emoji';
import { uploadCommentImage, getPresignedUrl } from '@api/comment';
import { ImageType } from '@typings/Image';
import './styles.scss';

// Register EmojiBlot to Quill
Quill.register('modules/emoji', Emoji);

const formats = ['bold', 'italic', 'underline', 'link', 'image', 'emoji'];

interface EditorProps {
  content: string;
  setImages: React.Dispatch<React.SetStateAction<ImageType[]>>;
  setContent: (content: string) => void;
}

const Editor: React.FC<EditorProps> = ({ setImages, content, setContent }) => {
  const quillRef = useRef<ReactQuill>(null);

  const handleImageUpload = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/jpeg, image/jpg, image/png, image/gif');
    input.click();

    input.onchange = async () => {
      const file = input.files ? input.files[0] : null;
      if (file) {
        const formData = new FormData();
        formData.append('files', file);
        try {
          const response = await uploadCommentImage(formData);
          if (response && response.status === 'success') {
            const imageData = response.data;
            const previewUrl = await getPresignedUrl(imageData.key);
            setImages((prev: ImageType[]) => [
              ...prev,
              { id: prev.length + 1, ...imageData, previewUrl },
            ]);
          }
        } catch (error) {
          console.error(error);
        }
      }
    };
  }, [setImages]);

  const handleLinkInsert = useCallback(() => {
    // eslint-disable-next-line no-alert
    const url = prompt('Enter the URL:');
    if (url) {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection();
        if (range) {
          if (range.length === 0) {
            // If no text is selected, insert the link as text
            quill.insertText(range.index, url, 'link', url);
          } else {
            // If text is selected, apply the link formatting
            quill.format('link', url);
          }
        }
      }
    }
  }, []);

  const modules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        ['link', 'image', 'emoji'],
      ],
      handlers: {
        image: handleImageUpload,
        link: handleLinkInsert,
      },
    },
    'emoji-toolbar': true, // Enable emoji toolbar
    'emoji-textarea': true, // Enable emoji in the textarea
    'emoji-shortname': true, // Use emoji shortnames like :smile:
  };

  const handleEditorChange = (value: string) => {
    setContent(value);
  };

  return (
    <div className="custom-editor divide-current bg-light-gray">
      <ReactQuill
        placeholder="Leave feedback, comments or queries here"
        value={content}
        onChange={handleEditorChange}
        modules={modules}
        formats={formats}
        ref={quillRef}
      />
    </div>
  );
};

export default Editor;
